import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import Checkbox from "Components/Form/Checkbox";
import InputRange from "Components/Form/InputRange";
import { useAppState } from "Context/StateContext";
import { useStorageBoxHook } from "Hooks/useStorageBoxActions";
import useYupValidation from "Hooks/useYupValidation";
import { defaultLiftvanConfig, LiftvanConfig } from "Models/liftvan.config";
import { Order } from "Models/order";
import { StorageBox, StorageBoxDto } from "Models/storage.box";
import { StorageBoxType } from "Models/storage.box.type";
import { useTranslation } from "react-i18next";
import LiftvanConfigService from "Services/liftvan.config.service";
import StorageBoxTypeService from "Services/storage.box.type.service";
import { handleGetConvertValue } from "Util/converts";
import { getMeasurement } from "Util/getMeasurement";
import * as yup from "yup";

import { LIFTVAN } from "../../../constants";
import { getMuiPopupContainer } from "../../../container/themes/dispatchTheme";
import Modal from "../Modal";
import useStyles, { CheckboxContainer, Title } from "./styles";

type StorageBoxSingleUseModalJob = Pick<Order, "id" | "reference">;

interface StorageBoxSingleUseModalProps {
  onSuccessCreate?: (response: StorageBox, variables?: StorageBoxDto, payload?: any) => void;
}

export interface StorageBoxSingleUseModalHandles {
  close: () => void;
  open: (job?: StorageBoxSingleUseModalJob, storageBoxType?: StorageBoxType, payload?: any) => void;
}

const StorageBoxSingleUseModalComponent: ForwardRefRenderFunction<
  StorageBoxSingleUseModalHandles,
  StorageBoxSingleUseModalProps
> = ({ onSuccessCreate }, ref) => {
  const classes = useStyles();

  const { useCreateStorageBox, useCreateLiftvanConfig, useUpdateLiftvanConfig } =
    useStorageBoxHook();

  const { currentUser } = useAppState();
  const measurement = getMeasurement();
  const { t: translate } = useTranslation();

  const [job, setJob] = useState<StorageBoxSingleUseModalJob | null>(null);
  const [storageBoxType, setStorageBoxType] = useState<StorageBoxType>();
  // It's any because we can send any data to share between the calling of the modal and the success on end
  const [payload, setPayload] = useState<any>();

  const [capacity, setCapacity] = useState<string | number>();
  const [createAnotherSingleUseBox, setCreateAnotherSingleUseBox] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [liftvanConfig, setLiftvanConfig] = useState<LiftvanConfig>({
    ...defaultLiftvanConfig,
    jobId: job?.id,
  });
  const [open, setOpen] = useState<boolean>(false);
  const [storageBoxNumber, setStorageBoxNumber] = useState<string>("");
  const [storageBoxTypes, setStorageBoxTypes] = useState<StorageBoxType[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const clearForm = () => {
    setCapacity(0);
    setCreateAnotherSingleUseBox(false);
    setStorageBoxNumber("");
    setStorageBoxType(undefined);
    setJob(null);
  };

  const onCloseModal = () => {
    clearForm();
    setOpen(false);
  };

  const onOpenModal = (
    job?: StorageBoxSingleUseModalJob,
    storageBoxType?: StorageBoxType,
    _payload?: any
  ) => {
    setStorageBoxType(storageBoxType);
    setJob(job || null);
    if (job && job.id) {
      setLiftvanConfig((prev) => ({
        ...prev,
        jobId: job.id,
      }));
    }
    setPayload(_payload);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({ open: onOpenModal, close: onCloseModal }));

  const storageBoxSchema = yup.object().shape({
    storageBoxNumber: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    storageBoxTypeId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const liftvanConfigSchema = yup.object().shape({
    prefix: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    howMany: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const {
    mutate: createStorageBox,
    isLoading: isCreatingSingleUseBox,
    isSuccess: singleUseBoxCreated,
  } = useCreateStorageBox((response, variables) => onSuccessCreate?.(response, variables, payload));

  const {
    mutate: createLiftvanConfig,
    isLoading: isCreatingLiftvanConfig,
    isSuccess: liftvanConfigCreated,
  } = useCreateLiftvanConfig();

  const {
    mutate: updateLiftvanConfig,
    isLoading: isUpdatingLiftvanConfig,
    isSuccess: liftvanConfigUpdated,
  } = useUpdateLiftvanConfig();

  useEffect(() => {
    if (storageBoxType?.name === LIFTVAN && liftvanConfig.capacity <= 0) {
      setLiftvanConfig((state) => ({
        ...(state || {}),
        capacity: storageBoxType?.capacity || 0,
      }));
    }
  }, [storageBoxType]);

  useEffect(() => {
    if (singleUseBoxCreated || liftvanConfigCreated || liftvanConfigUpdated) {
      if (createAnotherSingleUseBox) {
        setStorageBoxNumber("");
      } else {
        onCloseModal();
      }
    }
  }, [singleUseBoxCreated, liftvanConfigCreated, liftvanConfigUpdated]);

  useEffect(() => {
    if (liftvanConfigCreated || liftvanConfigUpdated) onCloseModal();
  }, [liftvanConfigCreated, liftvanConfigUpdated]);

  const storageBoxesFields = { storageBoxNumber, storageBoxTypeId: storageBoxType?.id };
  const liftvanConfigFields = { howMany: liftvanConfig?.howMany, prefix: liftvanConfig?.prefix };

  const isValid = (schema: yup.ObjectSchema<any>, fields: any) => {
    try {
      schema.validateSync(fields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const { isFetching: isFetchingStorageBoxTypes } = useQuery(
    ["storage.box.types.list", currentUser?.companyId],
    () =>
      StorageBoxTypeService.getAll({
        companyId: currentUser?.companyId,
        include: JSON.stringify({
          association: "storageBoxes",
          attributes: ["id"],
          limit: 1,
          where: { isActive: true, singleUse: false },
        }),
        isActive: "1",
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data) {
          setStorageBoxTypes(response.data.filter((type) => type.storageBoxes?.length === 0));
        }
      },
    }
  );

  const { isFetching: isFetchingLiftvanConfig } = useQuery(
    ["liftvan.config.get", job?.id],
    () => LiftvanConfigService.getByJobId(job?.id ?? 0),
    {
      enabled: !!job?.id,
      onSuccess: (response) => {
        if (response && response.data) {
          setLiftvanConfig(response.data);
        }
      },
    }
  );

  useEffect(() => {
    setCapacity(
      handleGetConvertValue(
        storageBoxType?.capacity || 0,
        currentUser?.measurement?.code || currentUser?.company?.measurement?.code || "m³"
      )
    );
  }, [storageBoxType]);

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (storageBoxType?.name === LIFTVAN) {
      if (isValid(liftvanConfigSchema, liftvanConfigFields)) {
        if (liftvanConfig.id) updateLiftvanConfig(liftvanConfig);
        else createLiftvanConfig(liftvanConfig);
      }
    } else {
      const storageBoxToSave: StorageBoxDto = {
        number: storageBoxNumber,
        singleUse: true,
        singleUseJobId: job?.id,
        storageBoxTypeId: storageBoxType?.id ?? 0,
        storageBoxType,
      };

      if (isValid(storageBoxSchema, storageBoxesFields)) {
        createStorageBox(storageBoxToSave);
      }
    }
  };

  useEffect(() => {
    if (liftvanConfig.autoNumber) setLiftvanConfig({ ...liftvanConfig, prefix: job?.reference });
    else setLiftvanConfig({ ...liftvanConfig, prefix: undefined });
  }, [liftvanConfig.autoNumber]);

  const modalTitle = (): string => {
    if (storageBoxType?.name === LIFTVAN) {
      if (liftvanConfig.id) return translate("editLiftvans");

      return translate("addLiftvans");
    }

    return translate("addStorageBox");
  };

  return (
    <Modal
      container={getMuiPopupContainer}
      actionButtonProps={{
        customColorSave: Color.SUCCESS,
        loading: isCreatingSingleUseBox || isCreatingLiftvanConfig || isUpdatingLiftvanConfig,
        onCancel: () => onCloseModal(),
        onSave: () => handleSubmit(),
        saveDisabled: isCreatingSingleUseBox || isCreatingLiftvanConfig || isUpdatingLiftvanConfig,
        textSave:
          storageBoxType?.name === LIFTVAN && liftvanConfig.id
            ? translate("save")
            : translate("add"),
      }}
      // customLeftButtons={
      //   storageBoxType?.name === LIFTVAN ? null : (
      //     <Checkbox
      //       label={translate("createAnotherSingleUseBox")}
      //       checked={createAnotherSingleUseBox}
      //       onChange={(_, checked) => setCreateAnotherSingleUseBox(checked)}
      //     />
      //   )
      // }
      handleClose={() => onCloseModal()}
      open={open}
      showActionButtons
      showLeftActionButtons
      title={modalTitle()}
      className={classes.modalWrapper}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={12}>
          <Title>
            {storageBoxType?.name === LIFTVAN ? "\u00A0" : translate("newSingleUseBox")}
          </Title>
        </Grid>
        <Grid item xs={12} md={5}>
          <Select
            data-cy="storageBoxType"
            disabled={isFetchingStorageBoxTypes}
            error={fieldHasError("storageBoxTypeId")}
            errorMessage={getErrorByField("storageBoxTypeId")}
            label={translate("type")}
            onChange={(event: SelectChangeEvent<any>) => {
              const id = Number(event.target.value);
              const type = storageBoxTypes.find((type) => type.id === id);

              setStorageBoxType(type);
            }}
            placeholder={`${translate("select")}...`}
            required
            value={storageBoxType?.id}
          >
            {storageBoxTypes?.map((storageBoxType) => (
              <MenuItem value={storageBoxType?.id} key={storageBoxType?.id}>
                {storageBoxType?.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {storageBoxType?.name === LIFTVAN ? (
          <>
            <Grid item xs={12} md={2}>
              <InputRange
                change={(value) => setLiftvanConfig({ ...liftvanConfig, howMany: Number(value) })}
                containerClassName={classes.containerInputRange}
                data-cy="howMany"
                disabled={isFetchingLiftvanConfig}
                error={fieldHasError("howMany")}
                errorMessage={getErrorByField("howMany")}
                label={translate("howMany")}
                minValue={defaultLiftvanConfig.howMany}
                required
                value={liftvanConfig?.howMany}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <CheckboxContainer>
                <Checkbox
                  checked={liftvanConfig?.autoNumber}
                  disabled={isFetchingLiftvanConfig}
                  label={translate("autoNumberThem")}
                  onChange={(_, checked) =>
                    setLiftvanConfig({ ...liftvanConfig, autoNumber: checked })
                  }
                />
              </CheckboxContainer>
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                data-cy="liftvanConfig.prefix"
                disabled={liftvanConfig?.autoNumber || isFetchingLiftvanConfig}
                error={fieldHasError("prefix")}
                errorMessage={getErrorByField("prefix")}
                label={translate("prefix")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setLiftvanConfig({ ...liftvanConfig, prefix: event.target.value })
                }
                placeholder={`${translate("typePrefix")}...`}
                required
                value={liftvanConfig.prefix}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Input
                disabled
                label={"\u00A0"}
                value={
                  liftvanConfig?.howMany
                    ? `1 ${translate("of")} ${liftvanConfig?.howMany}`
                    : "\u00A0"
                }
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Input
                data-cy="liftvanConfig.capacity"
                disabled={isFetchingLiftvanConfig}
                placeholder={"-"}
                label={`${translate("capacity")} (${measurement?.code})`}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setLiftvanConfig({ ...liftvanConfig, capacity: Number(event.target.value) })
                }
                type="number"
                value={liftvanConfig?.capacity}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={5}>
              <Input
                data-cy="storageBoxNumber"
                error={fieldHasError("storageBoxNumber")}
                errorMessage={getErrorByField("storageBoxNumber")}
                label={translate("prefix")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setStorageBoxNumber(event.target.value)
                }
                placeholder={`${translate("typeNumber")}...`}
                required
                value={storageBoxNumber}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Input
                data-cy="storageBoxTypeCapacity"
                placeholder={"-"}
                label={translate("capacity")}
                value={`${capacity} ${getMeasurement()?.code}`}
                disabled
              />
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};

export default forwardRef(StorageBoxSingleUseModalComponent);
